'use client';
import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react';
import { IconCloseBox } from '@moar/svg';
import useResponsive from '../hooks/use-responsive/use-responsive';
import { formatBytes, getImagesToCache, getDataUrlsToCache } from '@moar/utils';
import { useContextData } from '../context/data';
import { useIsOnline } from '../context/online';
import { useContextModalCacheDisplay } from '../context/modalCacheDisplay';
import { postToServiceWorker } from '../sw-message';

/* eslint-disable-next-line */
export interface ModalCacheProps {}

export function ModalCache(props: ModalCacheProps) {
    const [isOpen, setIsOpen] = useContextModalCacheDisplay();
    const { isMobileDesign } = useResponsive();
    const isOnline = useIsOnline();
    const data = useContextData();
    const [cacheResetState, setCacheResetState] = useState('Refresh Content');
    const [cacheImagesCount, setCacheImagesCount] = useState<number | null>(null);
    const [cachePagesCount, setCachePagesCount] = useState<number | null>(null);
    const [successImageMsg, setSuccessImageMsg] = useState<null | string>(null);
    // Refresh-content progress: pages (/_next/data JSON) + images cache in parallel.
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pagesRequested, setPagesRequested] = useState(false);
    const [pagesProgress, setPagesProgress] = useState<{ processed: number; total: number } | null>(null);
    const [imagesProgress, setImagesProgress] = useState<{ processed: number; total: number } | null>(null);
    const [pagesResult, setPagesResult] = useState<number | null>(null);
    const [imagesResult, setImagesResult] = useState<number | null>(null);

    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

        const handleMessage = async (event: any) => {
            console.log(`Handling message from SW!`, event.data);

            if (event.data?.type === 'RETURN_CACHE_IMAGES') {
                setCacheImagesCount(Array.isArray(event.data.payload) ? event.data.payload.length : 0);
            }
            if (event.data?.type === 'RETURN_CACHE_PAGES') {
                setCachePagesCount(event.data.payload.count);
            }
            if (event.data?.type === 'CACHE_PAGES_PROGRESS') {
                setPagesProgress({ processed: event.data.payload.processed, total: event.data.payload.total });
            }
            if (event.data?.type === 'CACHE_PAGES_DONE') {
                setPagesResult(event.data.payload.count);
                setCachePagesCount(event.data.payload.count);
            }
            if (event.data?.type === 'CACHE_IMAGES_PROGRESS') {
                setImagesProgress({ processed: event.data.payload.processed, total: event.data.payload.total });
            }
            if (event.data?.type === 'CACHE_IMAGES_DONE') {
                setImagesResult(event.data.payload.count);
                setCacheImagesCount(event.data.payload.count);
            }
        };
        // Listen to the SW's broadcast replies. Attach to navigator.serviceWorker (always
        // present) rather than window.serwist, which can be undefined/stale right after a new
        // SW activates — the cause of the "Loading…" hang.
        navigator.serviceWorker.addEventListener('message', handleMessage);

        return function cleanup() {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, []);

    useEffect(() => {
        setSuccessImageMsg(null);

        if (isOpen) {
            refreshCounts();
        }
    }, [isOpen]);

    // A refresh runs pages + images in parallel; it's complete only when both report DONE
    // (pages are skipped if there's no buildId). Compose the combined success message then.
    useEffect(() => {
        if (!isRefreshing) return;
        const pagesDone = !pagesRequested || pagesResult !== null;
        const imagesDone = imagesResult !== null;
        if (!pagesDone || !imagesDone) return;

        const parts: string[] = [];
        if (pagesRequested && pagesResult !== null) parts.push(`${pagesResult} pages`);
        if (imagesResult !== null) parts.push(`${imagesResult} images`);
        setSuccessImageMsg(`Done! Cached ${parts.join(' + ')}`);
        setCacheResetState('Refresh Content');
        setIsRefreshing(false);
        setPagesProgress(null);
        setImagesProgress(null);
    }, [isRefreshing, pagesRequested, pagesResult, imagesResult]);

    const handleCacheImages = () => {
        console.log('handleCacheImages', data);

        if (!data) {
            console.log('No data could mean CORS issue with localhost. Open Chrome with --disable-web-security');
            return;
        }
        setSuccessImageMsg(null);
        setCacheResetState('Loading...');
        setCacheImagesCount(null);
        setCachePagesCount(null);
        setIsRefreshing(true);
        setPagesProgress(null);
        setImagesProgress(null);
        setPagesResult(null);
        setImagesResult(null);
        const urls = getImagesToCache({ data, isMobile: isMobileDesign });

        // Also warm the page data (/_next/data/*.json) so object/section pages work offline
        // even if serwist's precache install dropped a few entries. buildId is on Next's
        // window.__NEXT_DATA__ (Pages Router).
        const buildId = (window as any).__NEXT_DATA__?.buildId as string | undefined;
        const dataUrls = buildId ? getDataUrlsToCache({ data, buildId }) : [];
        setPagesRequested(dataUrls.length > 0);
        if (dataUrls.length > 0) {
            postToServiceWorker({ command: 'log', message: 'cache pages', action: 'CACHE_PAGES', payload: dataUrls });
        }

        postToServiceWorker({ command: 'log', message: 'cache images', action: 'CACHE_IMAGES', payload: urls });
    };

    // Query the SW for how many images + page-data files are currently cached offline.
    const refreshCounts = () => {
        postToServiceWorker({ command: 'log', message: 'get cache images', action: 'GET_CACHE_IMAGES' });
        postToServiceWorker({ command: 'log', message: 'get cache pages', action: 'GET_CACHE_PAGES' });
    };

    const onClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog open={isOpen} onClose={() => onClose()} className="fixed inset-0 z-40">
            {/* The backdrop, rendered as a fixed sibling to the panel container */}
            <DialogBackdrop className="fixed inset-0 bg-black opacity-75" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <div className="relative w-full px-8 py-8 overflow-y-scroll md:max-w-md bg-ivory">
                    <div className="absolute z-50 right-6 top-6">
                        <button onClick={() => onClose()}>
                            <IconCloseBox />
                            <span className="sr-only">Close Modal</span>
                        </button>
                    </div>
                    <DialogTitle className="font-serif text-3xl transition-all md:text-6xl">App Settings</DialogTitle>
                    <div className="my-6">
                        <h2 className="mb-2 font-sans text-base font-bold">Content for Offline Use</h2>
                        <p className="mb-2 text-sm">
                            We recommend connecting to wifi instead of using cellular data before refreshing the
                            content.
                        </p>
                        <div className="mt-4 space-y-1 text-sm">
                            <div>Pages saved: {cachePagesCount ?? '—'}</div>
                            <div>Images saved: {cacheImagesCount ?? '—'}</div>
                        </div>
                    </div>
                    <div className="my-4">
                        {isRefreshing && (
                            <div className="mt-2 font-serif font-bold animate-pulse">
                                {pagesRequested && pagesResult === null && pagesProgress
                                    ? `Preparing pages ${pagesProgress.processed}/${pagesProgress.total}...`
                                    : imagesProgress
                                      ? `Loading images ${imagesProgress.processed}/${imagesProgress.total} (this may take a while)...`
                                      : 'Loading (this may take a while)...'}
                            </div>
                        )}
                        {successImageMsg && <div className="mt-2 font-bold">{successImageMsg}</div>}
                        {successImageMsg ? (
                            <button className="mt-2 btn btn--primary" onClick={() => onClose()}>
                                Close
                            </button>
                        ) : (
                            <button
                                disabled={!isOnline || isRefreshing}
                                className="mt-2 btn btn--primary"
                                onClick={() => handleCacheImages()}
                            >
                                {cacheResetState}
                            </button>
                        )}
                        {!isOnline && <p className="mt-2">You must be online to load content for offline use.</p>}
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default ModalCache;
